/**
 * Helpers for recurring calendar events, matching the Laravel contract in
 * conversional-laravel (App\Services\RecurringService, StoreRecurringRequest).
 *
 * A rule is always anchored to the event's start date — frequency-specific parts
 * (the weekly default weekday, the monthly day-of-month / nth-weekday) are derived
 * from the start rather than duplicated in state, so a rule travels with the
 * event's date instead of going stale if the start changes.
 *
 * A series' pattern is write-once via `POST /calendar-events/recurring` — it can
 * never be re-edited, but every occurrence carries it back read-only as
 * `recurring` (RecurringResource). {@link toRecurringPayload} serializes the UI
 * config for creation; {@link describeRecurring} renders the persisted shape.
 */
import moment, { Moment } from 'moment';

import {
  RecurrenceConfig,
  RecurrenceEndType,
  RecurrenceFrequency,
  RecurrenceMonthlyMode,
  RecurringModel,
  Weekday,
} from '@/typings/models/Recurrence';
import { CalendarEventModel } from '@/typings/models/ServiceRequest';

/** moment().day() index (0 = Sunday) → Weekday. */
const MOMENT_DAY_TO_WEEKDAY: Weekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const WEEKDAY_TO_MOMENT_DAY: Record<Weekday, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
};

/** Monday-to-Friday, used by the "Every weekday" preset. */
export const WEEKDAY_PRESET: Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

/** Is this event part of a recurring series? (CalendarEventResource#recurring). */
export function isRecurringEvent(event?: Partial<CalendarEventModel> | null): boolean {
  return Boolean(event?.recurring || event?.recurring_id);
}

/** The Weekday for a given date. */
export function weekdayForDate(date: Moment): Weekday {
  return MOMENT_DAY_TO_WEEKDAY[date.day()];
}

/**
 * The localized full weekday name (e.g. 'monday' → 'Monday'/'Montag'). Pass the
 * active app locale (`intl.locale`) so the name follows the user's language
 * selection rather than moment's global locale.
 */
export function weekdayLabel(weekday: Weekday, locale?: string): string {
  const day = moment().day(WEEKDAY_TO_MOMENT_DAY[weekday]);
  if (locale) day.locale(locale);
  return day.format('dddd');
}

/** The localized minimal weekday name for compact toggles (e.g. 'monday' → 'Mo'). */
export function weekdayShortLabel(weekday: Weekday, locale?: string): string {
  const day = moment().day(WEEKDAY_TO_MOMENT_DAY[weekday]);
  if (locale) day.locale(locale);
  return day.format('dd');
}

/** Which occurrence of its weekday the date is within its month (1-5). */
export function nthWeekdayOfMonth(date: Moment): number {
  return Math.ceil(date.date() / 7);
}

/** A sensible default config for a brand-new rule, anchored to `start`. */
export function defaultRecurrenceConfig(start?: Moment | null): RecurrenceConfig {
  const anchor = moment.isMoment(start) ? start : moment();
  return {
    frequency: RecurrenceFrequency.WEEKLY,
    interval: 1,
    byWeekdays: [weekdayForDate(anchor)],
    monthlyMode: RecurrenceMonthlyMode.DAY_OF_MONTH,
    end: { type: RecurrenceEndType.NEVER },
  };
}

/**
 * Serialize a {@link RecurrenceConfig} into the body `POST /calendar-events/recurring`
 * expects (minus title/assignee/lead/color/description/start/end, which the
 * caller already has). Anchored to `start`, the first occurrence.
 */
export function toRecurringPayload(config: RecurrenceConfig, start: Moment, timezone?: string) {
  const interval = config.interval && config.interval > 0 ? config.interval : 1;
  const payload: Record<string, unknown> = {
    frequency: config.frequency,
    interval,
    ...(timezone ? { timezone } : {}),
  };

  if (config.frequency === RecurrenceFrequency.WEEKLY) {
    payload.by_weekdays = config.byWeekdays?.length ? config.byWeekdays : [weekdayForDate(start)];
  }

  if (config.frequency === RecurrenceFrequency.MONTHLY) {
    const monthlyMode = config.monthlyMode || RecurrenceMonthlyMode.DAY_OF_MONTH;
    payload.monthly_mode = monthlyMode;
    if (monthlyMode === RecurrenceMonthlyMode.WEEKDAY) {
      payload.week_ordinal = nthWeekdayOfMonth(start);
      payload.weekday = weekdayForDate(start);
    } else {
      payload.day_of_month = config.dayOfMonth ?? start.date();
    }
  }

  if (config.end?.type === RecurrenceEndType.ON_DATE && config.end.until) {
    payload.until = moment(config.end.until).format('YYYY-MM-DD');
  } else if (config.end?.type === RecurrenceEndType.AFTER_OCCURRENCES && config.end.count) {
    payload.count = config.end.count;
  }

  return payload;
}

/** Normalize a config to a comparable string, e.g. to match it against a preset. */
export function recurrenceConfigKey(config?: RecurrenceConfig | null): string {
  if (!config) return '';
  const parts = [`FREQ=${config.frequency}`, `INTERVAL=${config.interval || 1}`];
  if (config.frequency === RecurrenceFrequency.WEEKLY) {
    parts.push(`BYDAY=${[...(config.byWeekdays || [])].sort().join(',')}`);
  }
  if (config.frequency === RecurrenceFrequency.MONTHLY) {
    const mode = config.monthlyMode || RecurrenceMonthlyMode.DAY_OF_MONTH;
    parts.push(`MODE=${mode}`);
    // An explicit day makes the rule distinct from the anchor-following preset.
    if (mode === RecurrenceMonthlyMode.DAY_OF_MONTH && config.dayOfMonth != null) {
      parts.push(`DAY=${config.dayOfMonth}`);
    }
  }
  const end = config.end || { type: RecurrenceEndType.NEVER };
  parts.push(`END=${end.type}`);
  if (end.type === RecurrenceEndType.ON_DATE && end.until) parts.push(`UNTIL=${moment(end.until).format('YYYY-MM-DD')}`);
  if (end.type === RecurrenceEndType.AFTER_OCCURRENCES && end.count) parts.push(`COUNT=${end.count}`);
  return parts.join(';');
}

type IntlShape = {
  formatMessage: (descriptor: { id: string; defaultMessage?: string }, values?: Record<string, any>) => string;
  /** The active app locale, e.g. 'de-DE' — drives weekday/date names below. */
  locale?: string;
};

/** The frequency-specific parts of a rule, already resolved — shared by the config- and server-shape describers. */
interface RecurrenceRuleParts {
  frequency: RecurrenceFrequency;
  interval: number;
  /** Weekly: the selected weekdays. */
  weekdays?: Weekday[];
  monthlyMode?: RecurrenceMonthlyMode;
  dayOfMonth?: number;
  /** 1-5, or -1 for the last week of the month. */
  weekOrdinal?: number;
  /** Monthly weekday-mode: the single weekday. */
  weekday?: Weekday;
  /** Series end: an ISO date… */
  until?: string;
  /** …or a number of occurrences. Never both. */
  count?: number;
}

function describeRuleParts(parts: RecurrenceRuleParts, intl: IntlShape): string {
  const { frequency, interval } = parts;
  const every = interval > 1;

  const ordinalLabel = (n: number) =>
    n === -1
      ? intl.formatMessage({ id: 'common.ordinal.last', defaultMessage: 'last' })
      : intl.formatMessage({ id: `common.ordinal.${n}`, defaultMessage: `${n}` });

  let rule: string;
  switch (frequency) {
    case RecurrenceFrequency.DAILY:
      rule = every
        ? intl.formatMessage({ id: 'pages.calendar.recurrence.everyNDays', defaultMessage: 'Every {n} days' }, { n: interval })
        : intl.formatMessage({ id: 'pages.calendar.recurrence.daily', defaultMessage: 'Daily' });
      break;

    case RecurrenceFrequency.WEEKLY: {
      const days = parts.weekdays || [];
      const isWeekdayPreset = days.length === WEEKDAY_PRESET.length && WEEKDAY_PRESET.every((d) => days.includes(d));
      if (isWeekdayPreset && !every) {
        rule = intl.formatMessage({ id: 'pages.calendar.recurrence.everyWeekday', defaultMessage: 'Every weekday (Monday to Friday)' });
        break;
      }
      const dayLabels = days.map((d) => weekdayLabel(d, intl.locale)).join(', ');
      rule = every
        ? intl.formatMessage({ id: 'pages.calendar.recurrence.everyNWeeksOn', defaultMessage: 'Every {n} weeks on {days}' }, { n: interval, days: dayLabels })
        : intl.formatMessage({ id: 'pages.calendar.recurrence.weeklyOn', defaultMessage: 'Weekly on {days}' }, { days: dayLabels });
      break;
    }

    case RecurrenceFrequency.MONTHLY: {
      if (parts.monthlyMode === RecurrenceMonthlyMode.WEEKDAY) {
        const ordinal = ordinalLabel(parts.weekOrdinal ?? 1);
        const weekday = weekdayLabel(parts.weekday || 'monday', intl.locale);
        rule = every
          ? intl.formatMessage({ id: 'pages.calendar.recurrence.everyNMonthsOnThe', defaultMessage: 'Every {n} months on the {ordinal} {weekday}' }, { n: interval, ordinal, weekday })
          : intl.formatMessage({ id: 'pages.calendar.recurrence.monthlyOnThe', defaultMessage: 'Monthly on the {ordinal} {weekday}' }, { ordinal, weekday });
      } else {
        rule = every
          ? intl.formatMessage({ id: 'pages.calendar.recurrence.everyNMonthsOnDay', defaultMessage: 'Every {n} months on day {day}' }, { n: interval, day: parts.dayOfMonth ?? 1 })
          : intl.formatMessage({ id: 'pages.calendar.recurrence.monthlyOnDay', defaultMessage: 'Monthly on day {day}' }, { day: parts.dayOfMonth ?? 1 });
      }
      break;
    }

    default:
      rule = intl.formatMessage({ id: 'pages.calendar.recurrence.custom', defaultMessage: 'Custom recurrence…' });
  }

  if (parts.until) {
    const until = moment(parts.until);
    if (intl.locale) until.locale(intl.locale);
    return intl.formatMessage(
      { id: 'pages.calendar.recurrence.untilDate', defaultMessage: '{rule}, until {date}' },
      { rule, date: until.format('ll') },
    );
  }
  if (parts.count) {
    return intl.formatMessage(
      { id: 'pages.calendar.recurrence.nTimes', defaultMessage: '{rule}, {n} times' },
      { rule, n: parts.count },
    );
  }
  return rule;
}

/**
 * Human-readable summary of a UI-side rule while it is being edited, e.g.
 * "Weekly on Monday", "Every 2 weeks on Monday, Wednesday, until 30 Sep 2026".
 * `start` provides the anchor for the weekly/monthly phrasing.
 */
export function describeRecurrence(
  config: RecurrenceConfig | null | undefined,
  start: Moment | null | undefined,
  intl: IntlShape,
): string {
  if (!config) {
    return intl.formatMessage({ id: 'pages.calendar.recurrence.none', defaultMessage: 'Does not repeat' });
  }
  const anchor = moment.isMoment(start) ? start : moment();
  return describeRuleParts(
    {
      frequency: config.frequency,
      interval: config.interval || 1,
      weekdays: config.byWeekdays && config.byWeekdays.length ? config.byWeekdays : [weekdayForDate(anchor)],
      monthlyMode: config.monthlyMode,
      dayOfMonth: config.dayOfMonth ?? anchor.date(),
      weekOrdinal: nthWeekdayOfMonth(anchor),
      weekday: weekdayForDate(anchor),
      until: config.end?.type === RecurrenceEndType.ON_DATE ? config.end.until : undefined,
      count: config.end?.type === RecurrenceEndType.AFTER_OCCURRENCES ? config.end.count : undefined,
    },
    intl,
  );
}

/**
 * Human-readable summary of a persisted series (CalendarEventResource#recurring).
 * Reads the discrete fields the backend stores instead of deriving them from an
 * anchor: monthly weekday-mode keeps its weekday in `by_weekdays[0]`
 * (RecurringService), and `week_ordinal` may be -1 ("last").
 */
export function describeRecurring(recurring: RecurringModel | null | undefined, intl: IntlShape): string {
  if (!recurring) {
    return intl.formatMessage({ id: 'pages.calendar.recurrence.none', defaultMessage: 'Does not repeat' });
  }
  const anchor = recurring.start_at ? moment(recurring.start_at) : moment();
  return describeRuleParts(
    {
      frequency: recurring.frequency,
      interval: recurring.interval || 1,
      weekdays: recurring.by_weekdays?.length ? recurring.by_weekdays : [weekdayForDate(anchor)],
      monthlyMode: recurring.monthly_mode || RecurrenceMonthlyMode.DAY_OF_MONTH,
      dayOfMonth: recurring.day_of_month ?? anchor.date(),
      weekOrdinal: recurring.week_ordinal ?? nthWeekdayOfMonth(anchor),
      weekday: recurring.by_weekdays?.[0] || weekdayForDate(anchor),
      until: recurring.until_at || undefined,
      count: recurring.count || undefined,
    },
    intl,
  );
}

export interface RecurrencePreset {
  key: string;
  label: string;
  /** The config for this preset, or `null` for "Does not repeat". */
  config: RecurrenceConfig | null;
  isCustom?: boolean;
}

/**
 * The quick-pick options shown in the recurrence dropdown, anchored to `start`.
 * Mirrors Google Calendar's preset list (minus yearly, which the backend doesn't
 * support). "Custom…" opens the detailed editor.
 */
export function getRecurrencePresets(start: Moment | null | undefined, intl: IntlShape): RecurrencePreset[] {
  const anchor = moment.isMoment(start) ? start : moment();
  const preset = (frequency: RecurrenceFrequency, extra: Partial<RecurrenceConfig> = {}): RecurrenceConfig => ({
    frequency,
    interval: 1,
    end: { type: RecurrenceEndType.NEVER },
    ...extra,
  });

  return [
    {
      key: 'none',
      config: null,
      label: intl.formatMessage({ id: 'pages.calendar.recurrence.none', defaultMessage: 'Does not repeat' }),
    },
    {
      key: 'daily',
      config: preset(RecurrenceFrequency.DAILY),
      label: intl.formatMessage({ id: 'pages.calendar.recurrence.daily', defaultMessage: 'Daily' }),
    },
    {
      key: 'weekly',
      config: preset(RecurrenceFrequency.WEEKLY, { byWeekdays: [weekdayForDate(anchor)] }),
      label: intl.formatMessage(
        { id: 'pages.calendar.recurrence.weeklyOn', defaultMessage: 'Weekly on {days}' },
        { days: weekdayLabel(weekdayForDate(anchor), intl.locale) },
      ),
    },
    {
      key: 'monthly',
      config: preset(RecurrenceFrequency.MONTHLY, { monthlyMode: RecurrenceMonthlyMode.DAY_OF_MONTH }),
      label: intl.formatMessage(
        { id: 'pages.calendar.recurrence.monthlyOnDay', defaultMessage: 'Monthly on day {day}' },
        { day: anchor.date() },
      ),
    },
    {
      key: 'weekdays',
      config: preset(RecurrenceFrequency.WEEKLY, { byWeekdays: WEEKDAY_PRESET }),
      label: intl.formatMessage({ id: 'pages.calendar.recurrence.everyWeekday', defaultMessage: 'Every weekday (Monday to Friday)' }),
    },
    {
      key: 'custom',
      config: null,
      isCustom: true,
      label: intl.formatMessage({ id: 'pages.calendar.recurrence.custom', defaultMessage: 'Custom recurrence…' }),
    },
  ];
}
